import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { NoPermission } from '@vindr/ui';
import { lazy } from 'react';

const HomeRouting = lazy(() => import('routes/auth/HomeRouting'));
const VisitStudy = lazy(() => import('#pages/visit-study'));
const GetStarted = lazy(() => import('#pages/get-started'));

const ViewerDocument = lazy(() => import('#pages/document'));

/* <Admin Settings> */
const AdminSettings = lazy(() => import('#pages/admin-settings'));
const Account = lazy(() => import('@vindr/workspace-core/modules/pages/account'));
const Connection = lazy(() => import('#pages/admin-settings/pages/connection'));
const CAD = lazy(() => import('@vindr/workspace-core/modules/pages/CAD'));
const Devices = lazy(() => import('@vindr/workspace-core/modules/pages/Devices'));
const QMS = lazy(() => import('@vindr/workspace-core/modules/pages/qms-management'));
const FilterManagement = lazy(
  () => import('@vindr/workspace-core/modules/pages/filter-management')
);
const Institution = lazy(() => import('@vindr/workspace-core/modules/pages/institution'));
const ProcedureGroup = lazy(() => import('@vindr/workspace-core/modules/pages/procedure-group'));
const Procedure = lazy(() => import('@vindr/workspace-core/modules/pages/procedure'));
const BodyPart = lazy(() => import('@vindr/workspace-core/modules/pages/body-part'));
const Department = lazy(() => import('@vindr/workspace-core/modules/pages/department'));
const Role = lazy(() => import('@vindr/workspace-core/modules/pages/role'));
const ShareSettings = lazy(() => import('#pages/admin-settings/pages/share-settings'));
const GroupAccount = lazy(() => import('@vindr/workspace-core/modules/pages/group-account'));
/* </Admin Settings> */

/* <Dashboard> */
const Dashboard = lazy(() => import('#pages/dashboard'));
// const Overview = lazy(() => import('#pages/dashboard/pages/overview'));
const ExamByRadioLogist = lazy(() => import('#pages/dashboard/pages/exam-by-radiologist'));
const PerformanceByRadiologist = lazy(
  () => import('#pages/dashboard/pages/performance-by-radiologist')
);
const DashboardOrder = lazy(() => import('#pages/dashboard/pages/dashboard-order'));
/* </Dashboard> */

const Order = lazy(() => import('#pages/order'));
const OrderWorklist = lazy(() => import('#pages/order-worklist'));
const Patient = lazy(() => import('@vindr/workspace-core/modules/pages/patient'));
// const Report = lazy(() => import('#pages/report'));
const Share = lazy(() => import('#pages/share'));
const StudyList = lazy(() => import('#pages/studylist'));
const Assigned = lazy(() => import('#pages/assigned'));

/* <User Settings> */
const UserSettings = lazy(() => import('#pages/user-settings'));
const LayoutReport = lazy(() => import('@vindr/workspace-core/modules/pages/layout-report'));
// const Profile = lazy(() => import('#pages/user-settings/pages/profile'));
// const StudyListSettings = lazy(() => import('#pages/user-settings/pages/studylist-settings'));
const TemplateReport = lazy(() => import('@vindr/workspace-core/modules/pages/template-report'));
const Element = lazy(() => import('#pages/user-settings/pages/element'));
const TemplateStructuredReport = lazy(
  () => import('#pages/user-settings/pages/template-structured-report')
);
const DigitalSign = lazy(() => import('#pages/user-settings/pages/monitoring/digital-sign'));
const HL7 = lazy(() => import('#pages/user-settings/pages/monitoring/hl7'));
const Consultation = lazy(() => import('#pages/user-settings/pages/monitoring/consultation'));
/* </User Settings> */
const QMSOverview = lazy(() => import('#pages/qms/Overview'));
const QMSDevice = lazy(() => import('#pages/qms/Device'));

const Upload = lazy(() => import('#pages/upload-v2'));

const baseRoute = {
  admin_settings: '/admin',
  user_settings: '/user',
  dashboard: '/dashboard',
};

export const AdminRoutesList = [
  {
    path: `${baseRoute.admin_settings}/*`,
    component: AdminSettings,
    permissions: [PERMISSIONS.page.admin_settings],
    children: [
      { path: `account`, component: Account },
      { path: `connection`, component: Connection },
      { path: `connection/CAD`, component: CAD },
      { path: `connection/devices`, component: Devices },
      { path: `connection/qms-management`, component: QMS },
      { path: `filter-management`, component: FilterManagement },
      { path: `institution`, component: Institution },
      { path: `procedure-group`, component: ProcedureGroup },
      { path: 'procedure', component: Procedure },
      { path: 'body-part', component: BodyPart },
      { path: 'department', component: Department },
      { path: 'role', component: Role },
      { path: 'share-settings', component: ShareSettings },
      { path: 'group-account', component: GroupAccount },
    ],
  },
];

export const UserRoutesList = [
  {
    path: `${baseRoute.user_settings}/*`,
    component: UserSettings,
    permissions: [PERMISSIONS.page.user_settings],
    children: [
      // { path: 'profile', component: Profile },
      // { path: 'studylist-setting', component: StudyListSettings },
      { path: 'layout-report', component: LayoutReport },
      { path: 'template-report', component: TemplateReport },
      { path: 'element', component: Element },
      { path: 'template-structured-report', component: TemplateStructuredReport },
      { path: 'monitoring/digital-sign', component: DigitalSign },
      { path: 'monitoring/hl7', component: HL7, permissions: [PERMISSIONS.page.monitoring_hl7] },
      { path: 'monitoring/consultation', component: Consultation },
    ],
  },
];

export const DashboardRoutesList = [
  {
    path: `${baseRoute.dashboard}/*`,
    component: Dashboard,
    permissions: [PERMISSIONS.page.view_dashboard],
    children: [
      // { path: 'overview', component: Overview },
      { path: 'performance-by-radiologist', component: PerformanceByRadiologist },
      { path: 'exam-by-radiologist', component: ExamByRadioLogist },
      { path: 'dashboard-order', component: DashboardOrder },
    ],
  },
];

const routesList: {
  path: string;
  component: React.LazyExoticComponent<() => JSX.Element> | (() => JSX.Element);
  children?: {
    path: string;
    component: React.LazyExoticComponent<() => JSX.Element>;
  }[];
  permissions?: string[];
}[] = [
  ...AdminRoutesList,
  ...DashboardRoutesList,
  {
    path: '/order',
    component: Order,
    permissions: [PERMISSIONS.page.view_order],
  },
  {
    path: '/worklist',
    component: OrderWorklist,
    permissions: [PERMISSIONS.page.view_worklist],
  },
  {
    path: '/assigned',
    component: Assigned,
    permissions: [PERMISSIONS.page.view_assignedlist],
  },
  {
    path: '/patient',
    component: Patient,
    permissions: [PERMISSIONS.page.view_patient],
  },
  {
    path: '/upload',
    component: Upload,
    permissions: [PERMISSIONS.page.view_upload],
  },
  {
    path: '/share',
    component: Share,
    permissions: [PERMISSIONS.page.view_sharedlist],
  },
  {
    path: '/studylist',
    component: StudyList,
    permissions: [PERMISSIONS.page.view_studylist],
  },
  ...UserRoutesList,
  {
    path: '/get-started',
    component: GetStarted,
  },
  {
    path: '/no-permission',
    component: NoPermission,
  },
  {
    path: '/document',
    component: ViewerDocument,
  },
  {
    path: '/visit-study',
    component: VisitStudy,
  },
  {
    path: '/qms-portal/overview',
    component: QMSOverview,
    customHeader: true,
  },
  {
    path: '/qms-portal/device',
    component: QMSDevice,
    customHeader: true,
  },
  {
    path: '/',
    component: HomeRouting,
  },
  // {
  //   path: '*',
  //   component: HomeRouting,
  // },
];

export default routesList;
